<template>
  <div 
    class="reviews-display-widget" 
    :style="containerStyle"
  >
    <!-- Title Section -->
    <div v-if="widget.title || widget.subtitle" class="reviews-header text-center mb-4">
      <h3 v-if="widget.title" class="reviews-title" :style="{ ...titleStyle, color: titleStyle.color || '#1a1a1a !important' }">
        <span style="color: #1a1a1a !important;">{{ widget.title }}</span>
      </h3>
      <p v-if="widget.subtitle" class="reviews-subtitle" style="color: #666 !important;">
        {{ widget.subtitle }}
      </p>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="text-center py-5">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Φόρτωση κριτικών...</span>
      </div>
    </div>

    <!-- Reviews Display -->
    <div v-else-if="reviews.length > 0" class="reviews-container">
      <!-- Grid Layout -->
      <div 
        v-if="widget.layout === 'grid'" 
        class="reviews-grid"
        :class="`grid-cols-${widget.columns || 3}`"
      >
        <div 
          v-for="review in displayedReviews" 
          :key="review.id"
          class="review-card"
        >
          <!-- Photo -->
          <div v-if="widget.showPhoto && review.photo" class="review-photo mb-3">
            <img 
              :src="getPhotoUrl(review.photo)" 
              :alt="review.name"
              class="review-image"
              @error="handleImageError"
            >
          </div>

          <!-- Rating Stars -->
          <div v-if="widget.showRating && review.rating" class="review-rating mb-2">
            <i 
              v-for="star in 5" 
              :key="star"
              class="bi"
              :class="star <= review.rating ? 'bi-star-fill' : 'bi-star'"
            ></i>
          </div>

          <!-- Comment -->
          <div class="review-comment mb-3">
            <p class="mb-0" style="color: #2c3e50 !important;">{{ review.comment }}</p>
          </div>

          <!-- Author Info -->
          <div class="review-author">
            <strong style="color: #1a1a1a !important; display: block;">{{ review.name }}</strong>
            <span v-if="widget.showDate" class="review-date" style="color: #999 !important; display: block; margin-top: 0.25rem;">
              {{ formatDate(review.review_date) }}
            </span>
          </div>
        </div>
      </div>

      <!-- Slider Layout -->
      <div v-else-if="widget.layout === 'slider'" class="reviews-slider">
        <div class="slider-container">
          <div 
            class="slider-track"
            :style="{ transform: `translateX(-${currentSlide * 100}%)` }"
          >
            <div 
              v-for="review in reviews" 
              :key="review.id"
              class="slider-item"
            >
              <div class="review-card-slider">
                <!-- Photo -->
                <div v-if="widget.showPhoto && review.photo" class="review-photo-slider mb-3">
                  <img 
                    :src="getPhotoUrl(review.photo)" 
                    :alt="review.name"
                    class="review-image-slider"
                    @error="handleImageError"
                  >
                </div>

                <!-- Rating Stars -->
                <div v-if="widget.showRating && review.rating" class="review-rating-slider mb-3">
                  <i 
                    v-for="star in 5" 
                    :key="star"
                    class="bi"
                    :class="star <= review.rating ? 'bi-star-fill' : 'bi-star'"
                  ></i>
                </div>

                <!-- Comment -->
                <div class="review-comment-slider mb-3">
                  <p class="mb-0" style="color: #2c3e50 !important;">{{ review.comment }}</p>
                </div>

                <!-- Author Info -->
                <div class="review-author-slider">
                  <strong style="color: #1a1a1a !important; display: block;">{{ review.name }}</strong>
                  <span v-if="widget.showDate" class="review-date" style="color: #999 !important; display: block; margin-top: 0.5rem;">
                    {{ formatDate(review.review_date) }}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <!-- Slider Controls -->
          <button 
            v-if="reviews.length > 1"
            class="slider-btn slider-btn-prev"
            @click="prevSlide"
            :disabled="currentSlide === 0"
          >
            <i class="bi bi-chevron-left"></i>
          </button>
          <button 
            v-if="reviews.length > 1"
            class="slider-btn slider-btn-next"
            @click="nextSlide"
            :disabled="currentSlide === reviews.length - 1"
          >
            <i class="bi bi-chevron-right"></i>
          </button>

          <!-- Slider Dots -->
          <div v-if="reviews.length > 1" class="slider-dots">
            <button
              v-for="(review, index) in reviews"
              :key="index"
              class="slider-dot"
              :class="{ active: currentSlide === index }"
              @click="goToSlide(index)"
            ></button>
          </div>
        </div>
      </div>

      <!-- List Layout -->
      <div v-else class="reviews-list">
        <div 
          v-for="review in displayedReviews" 
          :key="review.id"
          class="review-item"
        >
          <div class="review-item-content">
            <!-- Photo -->
            <div v-if="widget.showPhoto && review.photo" class="review-photo-list">
              <img 
                :src="getPhotoUrl(review.photo)" 
                :alt="review.name"
                class="review-image-list"
                @error="handleImageError"
              >
            </div>

            <div class="review-text">
              <!-- Rating Stars -->
              <div v-if="widget.showRating && review.rating" class="review-rating-list mb-2">
                <i 
                  v-for="star in 5" 
                  :key="star"
                  class="bi"
                  :class="star <= review.rating ? 'bi-star-fill' : 'bi-star'"
                ></i>
              </div>

              <!-- Comment -->
              <div class="review-comment-list mb-2">
                <p class="mb-0" style="color: #2c3e50 !important;">{{ review.comment }}</p>
              </div>

              <!-- Author Info -->
              <div class="review-author-list">
                <strong style="color: #1a1a1a !important; display: block;">{{ review.name }}</strong>
                <span v-if="widget.showDate" class="review-date" style="color: #999 !important; display: block; margin-top: 0.25rem;">
                  {{ formatDate(review.review_date) }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Empty State -->
    <div v-else class="empty-reviews text-center py-5">
      <i class="bi bi-star display-4 text-muted mb-3"></i>
      <p class="text-muted">Δεν υπάρχουν εγκεκριμένες κριτικές ακόμα.</p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'

interface Review {
  id: number
  name: string
  comment: string
  photo: string | null
  review_date: string
  rating: number | null
  status: 'pending' | 'approved' | 'rejected'
  created_at: string
}

interface ReviewsDisplayWidget {
  title?: string
  subtitle?: string
  layout?: 'grid' | 'slider' | 'list'
  columns?: number
  limit?: number
  showPhoto?: boolean
  showRating?: boolean
  showDate?: boolean
  backgroundColor?: string
  padding?: string
  titleColor?: string
  titleSize?: string
}

interface Props {
  widget: ReviewsDisplayWidget
}

const props = defineProps<Props>()

const reviews = ref<Review[]>([])
const loading = ref(true)
const currentSlide = ref(0)

const containerStyle = computed(() => {
  const styles: any = {}
  if (props.widget.backgroundColor) {
    styles.backgroundColor = props.widget.backgroundColor
  }
  if (props.widget.padding) {
    styles.padding = props.widget.padding
  }
  return styles
})

const titleStyle = computed(() => {
  const styles: any = {}
  if (props.widget.titleColor) {
    styles.color = props.widget.titleColor
  }
  if (props.widget.titleSize) {
    styles.fontSize = props.widget.titleSize
  }
  return styles
})

const displayedReviews = computed(() => {
  if (props.widget.limit && props.widget.limit > 0) {
    return reviews.value.slice(0, props.widget.limit)
  }
  return reviews.value
})

const fetchReviews = async () => {
  loading.value = true
  try {
    const response = await fetch('/backend/api/reviews.php?status=approved')
    const data = await response.json()
    
    if (data.success) {
      reviews.value = data.reviews || []
    } else {
      console.error('API returned error:', data.message)
    }
  } catch (error) {
    console.error('Error fetching reviews:', error)
  } finally {
    loading.value = false
  }
}

const getPhotoUrl = (photo: string) => {
  if (photo.startsWith('http')) {
    return photo
  }
  return `/backend/uploads/reviews/${photo}`
}

const handleImageError = (event: Event) => {
  const target = event.target as HTMLImageElement
  target.style.display = 'none'
}

const formatDate = (dateString: string) => {
  const date = new Date(dateString)
  return date.toLocaleDateString('el-GR', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  })
}

const prevSlide = () => {
  if (currentSlide.value > 0) {
    currentSlide.value--
  }
}

const nextSlide = () => {
  if (currentSlide.value < reviews.value.length - 1) {
    currentSlide.value++
  }
}

const goToSlide = (index: number) => {
  currentSlide.value = index
}

onMounted(() => {
  fetchReviews()
})
</script>

<style scoped>
.reviews-display-widget {
  padding: 3rem 0;
  background-color: transparent;
}

.reviews-header {
  margin-bottom: 3rem;
}

.reviews-title {
  font-size: 2.5rem;
  font-weight: 700;
  color: #1a1a1a !important;
  margin-bottom: 0.75rem;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
}

.reviews-subtitle {
  font-size: 1.2rem;
  color: #666 !important;
  font-weight: 400;
}

/* Grid Layout */
.reviews-grid {
  display: grid;
  gap: 2rem;
  margin-bottom: 2rem;
}

.reviews-grid.grid-cols-1 {
  grid-template-columns: 1fr;
}

.reviews-grid.grid-cols-2 {
  grid-template-columns: repeat(2, 1fr);
}

.reviews-grid.grid-cols-3 {
  grid-template-columns: repeat(3, 1fr);
}

.reviews-grid.grid-cols-4 {
  grid-template-columns: repeat(4, 1fr);
}

.review-card {
  background: #ffffff;
  border-radius: 16px;
  padding: 2rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
  border: 1px solid rgba(0, 0, 0, 0.05);
  position: relative;
  overflow: hidden;
}

.review-card::before {
  content: '"';
  position: absolute;
  top: -10px;
  left: 20px;
  font-size: 120px;
  color: rgba(59, 130, 246, 0.08);
  font-family: Georgia, serif;
  line-height: 1;
  z-index: 0;
}

.review-card:hover {
  transform: translateY(-8px);
  box-shadow: 0 8px 24px rgba(59, 130, 246, 0.15);
  border-color: rgba(59, 130, 246, 0.2);
}

.review-card > * {
  position: relative;
  z-index: 1;
}

.review-photo {
  text-align: center;
}

.review-image {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  object-fit: cover;
  border: 4px solid #f8f9fa;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.review-rating {
  color: #ffc107 !important;
  font-size: 1.3rem;
  display: flex;
  gap: 0.25rem;
  justify-content: center;
}

.review-rating i {
  margin-right: 0;
  filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.1));
  color: #ffc107 !important;
}

.review-comment {
  font-style: italic;
  color: #2c3e50 !important;
  line-height: 1.8;
  font-size: 1.05rem;
  font-weight: 400;
}

.review-comment p {
  color: #2c3e50 !important;
  margin-bottom: 0 !important;
}

.review-author {
  font-size: 1rem;
  color: #1a1a1a !important;
  font-weight: 600;
}

.review-author strong {
  color: #1a1a1a !important;
}

.review-date {
  font-size: 0.9rem;
  color: #999 !important;
  font-weight: 400;
}

/* Slider Layout */
.reviews-slider {
  position: relative;
  max-width: 900px;
  margin: 0 auto;
  padding: 0 60px;
}

.slider-container {
  position: relative;
  overflow: hidden;
  border-radius: 20px;
}

.slider-track {
  display: flex;
  transition: transform 0.5s cubic-bezier(0.4, 0, 0.2, 1);
}

.slider-item {
  min-width: 100%;
  padding: 0 1rem;
}

.review-card-slider {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-radius: 20px;
  padding: 3rem 2.5rem;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15);
  text-align: center;
  position: relative;
  overflow: hidden;
  border: 1px solid rgba(0, 0, 0, 0.08);
}

.review-card-slider::before {
  content: '"';
  position: absolute;
  top: -20px;
  left: 30px;
  font-size: 180px;
  color: rgba(59, 130, 246, 0.08);
  font-family: Georgia, serif;
  line-height: 1;
  z-index: 0;
}

.review-card-slider > * {
  position: relative;
  z-index: 1;
}

.review-photo-slider {
  display: flex;
  justify-content: center;
}

.review-image-slider {
  width: 100px;
  height: 100px;
  border-radius: 50%;
  object-fit: cover;
  border: 5px solid #ffffff;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
}

.review-rating-slider {
  color: #ffc107 !important;
  font-size: 1.8rem;
  display: flex;
  justify-content: center;
  gap: 0.3rem;
}

.review-rating-slider i {
  margin: 0;
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.2));
  color: #ffc107 !important;
}

.review-comment-slider {
  font-size: 1.2rem;
  font-style: italic;
  color: #2c3e50 !important;
  line-height: 1.9;
  max-width: 700px;
  margin: 0 auto;
  font-weight: 500;
}

.review-comment-slider p {
  color: #2c3e50 !important;
}

.review-author-slider {
  font-size: 1.1rem;
  color: #1a1a1a !important;
  font-weight: 700;
}

.review-author-slider strong {
  color: #1a1a1a !important;
}

.review-author-slider .review-date {
  color: #666 !important;
  font-weight: 400;
}

.slider-btn {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background: rgba(255, 255, 255, 0.95);
  border: none;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.3rem;
  color: #3b82f6;
  z-index: 10;
}

.slider-btn:hover:not(:disabled) {
  background: #ffffff;
  transform: translateY(-50%) scale(1.15);
  box-shadow: 0 6px 20px rgba(59, 130, 246, 0.3);
}

.slider-btn:disabled {
  opacity: 0.2;
  cursor: not-allowed;
}

.slider-btn-prev {
  left: 10px;
}

.slider-btn-next {
  right: 10px;
}

.slider-dots {
  display: flex;
  justify-content: center;
  gap: 0.6rem;
  margin-top: 2rem;
}

.slider-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: rgba(59, 130, 246, 0.3);
  border: none;
  cursor: pointer;
  transition: all 0.3s ease;
  padding: 0;
}

.slider-dot:hover {
  background: rgba(59, 130, 246, 0.6);
}

.slider-dot.active {
  background: #3b82f6;
  width: 36px;
  border-radius: 6px;
}

/* List Layout */
.reviews-list {
  display: flex;
  flex-direction: column;
  gap: 2rem;
}

.review-item {
  background: #ffffff;
  border-radius: 16px;
  padding: 2rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
  border-left: 4px solid #3b82f6;
}

.review-item:hover {
  box-shadow: 0 8px 24px rgba(59, 130, 246, 0.15);
  transform: translateX(4px);
}

.review-item-content {
  display: flex;
  gap: 2rem;
  align-items: flex-start;
}

.review-photo-list {
  flex-shrink: 0;
}

.review-image-list {
  width: 70px;
  height: 70px;
  border-radius: 50%;
  object-fit: cover;
  border: 3px solid #f8f9fa;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.review-text {
  flex: 1;
}

.review-rating-list {
  color: #ffc107 !important;
  font-size: 1.1rem;
  display: flex;
  gap: 0.2rem;
}

.review-rating-list i {
  margin-right: 0;
  filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.1));
  color: #ffc107 !important;
}

.review-comment-list {
  color: #2c3e50 !important;
  line-height: 1.8;
  font-size: 1.05rem;
}

.review-comment-list p {
  color: #2c3e50 !important;
}

.review-author-list {
  font-size: 1rem;
  color: #1a1a1a !important;
  font-weight: 600;
}

.review-author-list strong {
  color: #1a1a1a !important;
}

.review-author-list .review-date {
  color: #999 !important;
  font-weight: 400;
}

/* Empty State */
.empty-reviews {
  padding: 4rem 2rem;
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  border-radius: 20px;
  text-align: center;
}

.empty-reviews i {
  color: #3b82f6;
  opacity: 0.6;
  margin-bottom: 1.5rem;
}

.empty-reviews p {
  color: #5a6c7d !important;
  font-size: 1.2rem;
  font-weight: 500;
}

/* Responsive */
@media (max-width: 992px) {
  .reviews-grid.grid-cols-3,
  .reviews-grid.grid-cols-4 {
    grid-template-columns: repeat(2, 1fr);
  }
  
  .reviews-slider {
    padding: 0 50px;
  }
}

@media (max-width: 768px) {
  .reviews-grid.grid-cols-2,
  .reviews-grid.grid-cols-3,
  .reviews-grid.grid-cols-4 {
    grid-template-columns: 1fr;
  }

  .reviews-title {
    font-size: 2rem;
  }
  
  .reviews-subtitle {
    font-size: 1rem;
  }

  .slider-btn-prev {
    left: 5px;
  }

  .slider-btn-next {
    right: 5px;
  }
  
  .slider-btn {
    width: 40px;
    height: 40px;
    font-size: 1.1rem;
  }

  .review-item-content {
    flex-direction: column;
    text-align: center;
    gap: 1.5rem;
  }

  .review-photo-list {
    display: flex;
    justify-content: center;
  }
  
  .review-card-slider {
    padding: 2.5rem 1.5rem;
  }
  
  .review-comment-slider {
    font-size: 1.1rem;
  }
  
  .reviews-slider {
    padding: 0 45px;
  }
}
</style>
